import * as tslib_1 from "tslib";
import _, { uniqueId } from 'lodash';
import dayjs from 'dayjs';
import { OptionType, AssetType, OptionExpirationType, OrderStatuses, OrderType, OrderStrategyTypeId, } from '@tradingblock/types';
import { OrderAction, PositionEffect, OrderActionDirection, OrderClass, Durations as DurationValues, Durations, DebitOrCredit, } from '@tradingblock/types';
import { isIndexSymbol, parseOccSymbol, isOptionAsset } from './SymbolUtilities';
import { Strategies } from '../strategies/strategies';
import { CustomStrategy } from '../strategies/CustomStrategy';
import { isBetweenInclusive } from './DateUtilities';
export const OCCSymbolToOptionData = parseOccSymbol;
export const DeriveLegAssetType = (leg) => {
    if (_.isNil(leg.OptionType) === false) {
        return AssetType.Option;
    }
    else if (isIndexSymbol(leg.UnderlyingSymbol)) {
        return AssetType.Index;
    }
    else {
        return AssetType.Equity;
    }
};
export const GetOCCSymbol = (symbolData) => {
    const { underlyingSymbol, expiration, optionType, strike, prefixSymbolName } = symbolData;
    if (expiration === undefined || strike === undefined || optionType === undefined) {
        return underlyingSymbol;
    }
    const otype = optionType === OptionType.Put ? 'P' : 'C';
    let rootSymbol = underlyingSymbol;
    rootSymbol = _.replace(underlyingSymbol, '$', '');
    const doNotAddWeek = ['NANOS', 'XSP'];
    if (isIndexSymbol(underlyingSymbol)) {
        // rootSymbol = _.replace(underlyingSymbol, '$', '');
        if (expiration.optionExpirationType === OptionExpirationType.PM && !doNotAddWeek.includes(rootSymbol)) {
            rootSymbol = `${rootSymbol}W`;
        }
    }
    const dateVal = dayjs(expiration.date).format('YYMMDD');
    const strikeVal = strike * 1000;
    const prefix = prefixSymbolName ? '.' : '';
    return `${prefix}${rootSymbol} ${dateVal}${otype}${strikeVal}`;
};
export const orderLegOccSymbol = (underlyingSymbol, leg) => {
    const { Expiration, OptionType, Strike } = leg;
    let symbolOcc = undefined;
    if (!OptionType || leg.AssetType === AssetType.Equity) {
        symbolOcc = underlyingSymbol.symbol;
    }
    else if (OptionType && Expiration && Strike) {
        symbolOcc = GetOCCSymbol({
            underlyingSymbol: underlyingSymbol.symbol,
            symbolType: underlyingSymbol.type,
            expiration: Expiration,
            optionType: OptionType,
            strike: Strike,
        });
    }
    else {
        symbolOcc = undefined;
    }
    return symbolOcc;
};
export const toPrefixedOccSymbol = (symbol) => (_.first(symbol) === '.' ? symbol : `.${symbol}`);
export const toRawOccSymbol = (symbol) => _.isNil(symbol) ? '' : _.first(symbol) === '.' ? _.replace(symbol, '.', '') : symbol;
export const AllStrategies = [...Strategies, CustomStrategy];
export const getStrategy = (name) => _.find(AllStrategies, s => s.info.Name === name) || CustomStrategy;
export const getKnownStrategy = (name) => _.find(Strategies, s => s.info.Name === name);
export const buildOrderLeg = (partOrd, orderAction, assetType, positionEffect, optionType, ratio) => {
    return {
        Symbol: partOrd.Symbol,
        UnderlyingSymbol: partOrd.UnderlyingSymbol || partOrd.Symbol,
        Id: uniqueId(),
        SpreadRatio: ratio,
        AssetType: assetType,
        Action: orderAction,
        PositionEffect: positionEffect,
        OptionType: optionType,
    };
};
export const transformForApi = (orderDesc) => {
    let transformedOrder = orderDesc.kind === 'simple'
        ? {
            AccountId: orderDesc.accountId,
            SubaccountId: orderDesc.subaccountId,
            UnderlyingSymbol: '',
            Legs: [
                {
                    AssetType: orderDesc.assetType,
                    Symbol: orderDesc.symbol,
                    Action: orderDesc.action,
                    PositionEffect: orderDesc.effect,
                },
            ],
            ClientRefId: orderDesc.referenceId,
            OrderClass: OrderClass.Single,
            Quantity: orderDesc.quantity,
            OrderType: orderDesc.orderType,
            Price: orderDesc.price,
            Stop: orderDesc.stop,
            AllOrNone: false,
            Duration: DurationValues.Day,
            Description: '',
            DebitCredit: DebitOrCredit.Debit,
        }
        : Object.assign({}, orderDesc, { Legs: _.map(orderDesc.Legs, l => {
                return Object.assign({}, l, { Symbol: l.OccSymbol || l.Symbol });
            }) });
    const isSpreadOrder = _.keys(transformedOrder.Legs).length > 1 ? true : false;
    const DebitCredit = isSpreadOrder ? transformedOrder.DebitCredit : undefined;
    const finalorder = Object.assign({}, transformedOrder, { DebitCredit });
    return transformOrderForApi(finalorder);
};
const getCallPutValue = (l) => {
    const legSymbol = l.OccSymbol;
    if (legSymbol === undefined) {
        return null;
    }
    const symInfo = parseOccSymbol(legSymbol);
    if (isOptionAsset(symInfo)) {
        return symInfo.option === OptionType.Call ? true : false;
    }
    return null;
};
function isPartialOrderWithLegs(order) {
    const ordLegs = _.get(order, 'Legs', undefined);
    return order && _.isArray(ordLegs) ? true : false;
}
export function transformOrderForApi(order) {
    const Price = Math.abs(order.Price);
    if (isPartialOrderWithLegs(order)) {
        const Legs = _.map(order.Legs, l => {
            return Object.assign({}, l, { CallPut: getCallPutValue(l) });
        });
        return Object.assign({}, order, { Price,
            Legs });
    }
    return Object.assign({}, order, { Price });
}
/**
 * Coerce string enum names from newer HTTP JSON into numeric enum values the UI expects
 * (Activity formatters, comparisons to OrderStatuses.*, PositionEffect[leg.PositionEffect], etc.).
 */
function coerceNumericEnumMember(enumObject, value, fallback) {
    if (value === null || value === undefined) {
        return fallback;
    }
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
    }
    if (typeof value === 'string') {
        if (/^-?\d+$/.test(value)) {
            return parseInt(value, 10);
        }
        const byName = enumObject[value];
        if (typeof byName === 'number') {
            return byName;
        }
    }
    return fallback;
}
/** Map API / display strings that are not OrderStatuses keys to the correct code */
const ORDER_STATUS_STRING_ALIASES = {
    Open: OrderStatuses.New,
    Working: OrderStatuses.New,
};
function coerceOrderStatus(value) {
    if (value === null || value === undefined) {
        return undefined;
    }
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
    }
    if (typeof value === 'string') {
        if (/^-?\d+$/.test(value)) {
            return parseInt(value, 10);
        }
        if (Object.prototype.hasOwnProperty.call(OrderStatuses, value)) {
            return OrderStatuses[value];
        }
        const aliased = ORDER_STATUS_STRING_ALIASES[value];
        if (typeof aliased === 'number') {
            return aliased;
        }
    }
    return undefined;
}
/**
 * Activity / selectors often do `parseInt(StrategyType)` — API may send strategy name (e.g. "Stock").
 * Map to {@link OrderStrategyTypeId} when possible.
 */
function coerceStrategyTypeToId(value) {
    if (value === null || value === undefined) {
        return undefined;
    }
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
    }
    if (typeof value === 'string') {
        if (/^-?\d+$/.test(value)) {
            return parseInt(value, 10);
        }
        const id = OrderStrategyTypeId[value];
        if (typeof id === 'number') {
            return id;
        }
    }
    return undefined;
}
function normalizeLegFromApi(leg) {
    const raw = leg;
    const symbolSource = leg.Symbol != null && leg.Symbol !== '' ? leg.Symbol : raw.symbol;
    const occSource = leg.OccSymbol != null && leg.OccSymbol !== '' ? leg.OccSymbol : raw.occSymbol;
    const optionRaw = raw.OptionType != null ? raw.OptionType : raw.optionType;
    const optionCoerced = optionRaw == null
        ? undefined
        : coerceNumericEnumMember(OptionType, optionRaw, typeof optionRaw === 'number' && !Number.isNaN(optionRaw) ? optionRaw : undefined);
    return Object.assign({}, leg, { Symbol: symbolSource, OccSymbol: occSource, AssetType: coerceNumericEnumMember(AssetType, leg.AssetType != null ? leg.AssetType : raw.assetType, leg.AssetType), Action: coerceNumericEnumMember(OrderAction, leg.Action != null ? leg.Action : raw.action, leg.Action), PositionEffect: coerceNumericEnumMember(PositionEffect, leg.PositionEffect != null ? leg.PositionEffect : raw.positionEffect, leg.PositionEffect) }, (optionCoerced !== undefined ? { OptionType: optionCoerced } : {}));
}
function normalizeOrderEventFromApi(ev) {
    const e = ev;
    const rawTs = e.TimeStamp != null ? e.TimeStamp : e.timeStamp;
    const timeStamp = rawTs instanceof Date
        ? rawTs
        : rawTs != null
            ? new Date(rawTs)
            : new Date(NaN);
    const statusRaw = e.OrderStatus != null ? e.OrderStatus : e.orderStatus;
    const coercedStatus = coerceOrderStatus(statusRaw);
    return {
        FillQuantity: _.defaultTo(e.FillQuantity, _.defaultTo(e.fillQuantity, 0)),
        Description: (_.isNil(e.Description) ? e.description : e.Description),
        FillPrice: _.defaultTo(e.FillPrice, _.defaultTo(e.fillPrice, 0)),
        Limit: _.defaultTo(e.Limit, _.defaultTo(e.limit, 0)),
        OrderStatus: coercedStatus !== undefined ? coercedStatus : 0,
        Stop: _.defaultTo(e.Stop, _.defaultTo(e.stop, 0)),
        Symbol: e.Symbol != null && e.Symbol !== '' ? e.Symbol : e.symbol,
        TimeStamp: timeStamp,
    };
}
export function normalizeOrderFromApi(order) {
    const raw = order;
    const { orderEvents: lowerOrderEvents } = raw, orderWithoutLowerOrderEvents = tslib_1.__rest(raw, ["orderEvents"]);
    const eventsSource = raw.OrderEvents != null ? raw.OrderEvents : lowerOrderEvents;
    const normalizedOrderEvents = eventsSource !== undefined ? _.map([...eventsSource], normalizeOrderEventFromApi) : undefined;
    const strategyId = coerceStrategyTypeToId(order.StrategyType);
    const normalizedLegs = _.map(order.Legs, normalizeLegFromApi);
    return Object.assign({}, orderWithoutLowerOrderEvents, (normalizedOrderEvents !== undefined ? { OrderEvents: normalizedOrderEvents } : {}), { OrderStatus: coerceOrderStatus(order.OrderStatus), OrderClass: coerceNumericEnumMember(OrderClass, order.OrderClass, order.OrderClass), OrderType: coerceNumericEnumMember(OrderType, order.OrderType, order.OrderType), StrategyType: (strategyId != null ? strategyId : order.StrategyType), Legs: normalizedLegs });
}
export const transformFromApi = (order) => {
    const base = normalizeOrderFromApi(order);
    const Duration = getDurationFromCode(base.Duration);
    return Object.assign({}, base, { Duration, Legs: _.map(base.Legs, l => {
            return Object.assign({}, l, { Symbol: toRawOccSymbol(l.Symbol), OccSymbol: toPrefixedOccSymbol(l.Symbol) });
        }) });
};
const getDurationFromCode = (duration) => {
    if (_.isNumber(duration)) {
        const durationCode = _.toNumber(duration);
        switch (durationCode) {
            case 1:
                return Durations.Day;
            case 2:
                return Durations.GTC;
            case 3:
                return Durations.Manual;
        }
    }
    return duration;
};
export const BuildInitialOrder = (strategy, partOrd) => ({
    OrderClass: strategy.profile.OrderClass,
    Legs: _.map(strategy.profile.Legs, (leg, ind) => {
        let legAction = leg.Direction === OrderActionDirection.Long ? OrderAction.Buy : OrderAction.Sell;
        if (leg.Direction === undefined) {
            legAction = ind === 0 ? OrderAction.Buy : OrderAction.Sell;
        }
        return Object.assign({}, leg, buildOrderLeg(partOrd, legAction, leg.AssetType, PositionEffect.Open, leg.OptionType, leg.SpreadRatio));
    }),
});
export const getNextLegId = (existingLegs) => {
    let id = _.uniqueId('leg_');
    while (_.keys(existingLegs).includes(id)) {
        id = _.uniqueId('leg_');
    }
    return id;
};
export const orderToReplaceOrder = (order) => {
    const valOrUndef = (val) => (val && val !== 0 ? val : undefined);
    return {
        AccountId: order.AccountId,
        DebitCredit: order.DebitCredit,
        SubaccountId: valOrUndef(order.SubaccountId),
        OrderId: order.OrderId,
        ClientRefId: valOrUndef(order.ClientRefId),
        OrderType: order.OrderType,
        Price: order.Price,
        Stop: valOrUndef(order.Stop),
    };
};
export const OnlyFilledOrders = (orders) => {
    return _(orders)
        .filter(o => {
        const orderStatus = o.OrderStatus;
        switch (orderStatus) {
            case OrderStatuses.PartiallyFilled:
            case OrderStatuses.Filled: {
                return true;
            }
            default:
                return false;
        }
    })
        .value();
};
export const FilterOrderByDate = (orders, startDate, endDate) => {
    return _.filter(orders, (order) => {
        if (startDate === undefined) {
            return true;
        }
        const endDateVal = endDate || new Date();
        return isBetweenInclusive(order.Date, startDate, endDateVal) ? true : false;
    });
};
export const TodaysFilledOrders = (orders) => {
    const startDate = dayjs(new Date())
        .hour(0)
        .toDate();
    const endDate = dayjs(new Date()).toDate();
    return OnlyFilledOrders(FilterOrderByDate(orders, startDate, endDate));
};
export const GetSymbolsFromOrders = (orders) => {
    return _.reduce(orders, (acc, o) => {
        const { Legs } = o;
        const orderSymbols = Legs ? _.map(Legs, l => l.Symbol) : [];
        const updated = _(acc)
            .union(orderSymbols)
            .uniq()
            .value();
        return updated;
    }, []);
};
const toOrderLegWithMetaData = (o, l) => {
    return Object.assign({}, l, { UnderlyingSymbol: o.UnderlyingSymbol, OrderId: o.OrderId, Date: o.Date });
};
const toOrderLegsWithMetaData = (orders) => {
    return _.flatMap(orders, o => o.Legs.map(l => toOrderLegWithMetaData(o, l)));
};
export const orderutility = {
    toOrderLegsWithMetaData: toOrderLegsWithMetaData,
};
export const isSymbolExpired = (symbol) => {
    const symbData = parseOccSymbol(symbol);
    //not expired
    if (symbData.expiration && symbData.expiration.valueOf() <= new Date().valueOf()) {
        return true;
    }
    return false;
};
export const getDefaultOrderQuantity = (strategy, { defaultQuantityForOptionsAndStrategies, defaultQuantityForShares }) => {
    if (strategy === 'Custom' || strategy === undefined) {
        return 1;
    }
    else if (strategy === 'Shares') {
        return defaultQuantityForShares || 1;
    }
    else {
        return defaultQuantityForOptionsAndStrategies || 1;
    }
};
export const getCustomStrategyLegQuantity = (assetType, { defaultQuantityForOptionsAndStrategies, defaultQuantityForShares }) => {
    if (assetType === AssetType.Equity) {
        return defaultQuantityForShares || 1;
    }
    else {
        return defaultQuantityForOptionsAndStrategies || 1;
    }
};
export const findDuplicateLegs = (legs) => {
    const partialLegs = _.map(legs, l => {
        const symbolData = parseOccSymbol(l.Symbol);
        const data = {
            spreadRatio: l.SpreadRatio,
            expiration: symbolData.expiration,
            strike: symbolData.strike,
            optionType: symbolData.option,
        };
        return {
            symbol: l.Symbol,
            occSymbol: l.OccSymbol,
            data,
        };
    });
    return _.filter(partialLegs, (curr, currInd) => {
        var eq = _.find(partialLegs, (e, eInd) => _.isEqual(e.data, curr.data) && eInd !== currInd);
        return eq ? true : false;
    });
};
//# sourceMappingURL=OrderUtilities.js.map